// Core shared types for Task Health platform
export { brandId, unwrapId } from "./ids.js";
// ─── Phone utilities (E.164) ─────────────────────────────
export { toE164, tryToE164, isValidUsPhone, formatPhoneDisplay, formatPhoneForPdf, isPhone } from "./phone.js";
export { PARTNER_BANK_ROUTING_NUMBERS, UNSUPPORTED_BANK_MESSAGE, CHIME_CONFIRMATION_LABEL, isChimeBankName, isPartnerBankRouting, partnerBankFor, partnerBankWarning, checkBankAnswer } from "./unsupported-bank.js";
export { RN_BONUS_AMOUNT_CENTS, RN_BONUS_LABELS, RN_BONUS_VISITS_REQUIRED, RN_BONUS_TAIL_DAYS, rnBonusDaysBetween, rnBonusDeadline, rnBonusDeadlineMet } from "./rn-bonus-rules.js";
export { ok, err, AppErrors, isOk, isErr, errorToHttpStatus } from "./result.js";
// ─── Date utilities ───────────────────────────────────────
export { parseDate, parseDateTime, parseDateTimeInTz, formatInTz, formatDateOnly, formatUTC, formatDate, buildDateReferenceBlock, toUTC, toTimezone, startOfDayInTz, endOfDayInTz, isValid, addDays, addHours, addMinutes, differenceInMinutes, differenceInHours, differenceInDays, isBefore, isAfter, isEqual } from "./dates.js";
// ─── Document types ───────────────────────────────────────
export { DOCUMENT_TYPES, DOCUMENT_LABELS, DOCUMENT_ORDER, formatDocumentName, formatPocLabel, normalizeToDocumentType } from "./documents.js";
// ─── Live translation ─────────────────────────────────────
export { LIVE_TRANSLATION_LANGUAGES, LIVE_TRANSLATION_TURN_ROLES, LIVE_TRANSLATION_SESSION_STATUSES, LIVE_TRANSLATION_PROVIDERS, GEMINI_LIVE_TRANSLATE_MODEL, GEMINI_LIVE_INTERPRETER_MODEL, GEMINI_LIVE_VOICE_NAME, GEMINI_LIVE_VAD_SILENCE_MS, GEMINI_LIVE_VAD_PREFIX_MS, GEMINI_LIVE_AUDIO_INPUT_RATE, GEMINI_LIVE_AUDIO_OUTPUT_RATE, GEMINI_LIVE_WS_HOST, GEMINI_LIVE_WS_PATH, GEMINI_LIVE_WS_PATH_API_KEY, NURSE_LANGUAGE_CODE, isSupportedLanguage } from "./liveTranslation.js";
// ─── PDF field mappings ───────────────────────────────────
export { PDF_MAPPINGS, getPdfMapping, FUNCTIONAL_LIMITATIONS_OPTIONS, ACTIVITIES_PERMITTED_OPTIONS, SAFETY_MEASURES_OPTIONS, FALL_RISK_FACTOR_OPTIONS } from "./pdf-field-mappings.js";
/**
 * Universal competency items — always shown on the Supervisory Evaluation
 * regardless of which POC duties were selected. Fixed list per regulatory requirements.
 */
export const SUPERVISORY_COMPETENCIES = [
    "Demonstrates proper hand hygiene",
    "Uses Standard Precautions",
    "Maintains safe, hazard-free environment",
    "Ensures client safety during ADLs",
    "Reports changes in client condition",
    "Follows written Plan of Care",
    "Completes required documentation",
    "Respects client's rights, privacy, dignity",
    "Maintains confidentiality (HIPAA)",
    "Demonstrates proper body mechanics",
    "Wears ID badge and follows dress code",
    "Conducts self professionally",
    "Develops rapport with client/family",
    "Explains procedures to client",
    "Demonstrates punctuality/dependability"
];
/**
 * Max number of competencies an agency may flag as "Observe Mandatory" for any
 * single service type. Kept at 3 to match the Supervisory Evaluation's
 * "pick exactly 3 direct-observation competencies" rule (MAX_DIRECT_OBS on
 * mobile) — so the mandatory pre-selected set can never exceed the 3 slots.
 */
export const MAX_MANDATORY_COMPETENCIES = 3;
// --- Plan of Care Types ---
export const POC_FREQUENCIES = [
    "Every visit",
    "As needed",
    "1 visits per week",
    "2 visits per week",
    "3 visits per week",
    "4 visits per week",
    "5 visits per week",
    "6 visits per week",
    "7 visits per week"
];
/**
 * Which shift of a split-shift Plan of Care a duty may land on. Configured per
 * duty by an admin (and optionally overridden per agency); consulted only when
 * a visit has two POC phases.
 *
 *   day   — AM POC only
 *   night — PM POC only
 *   both  — copied onto BOTH POCs when the duty is selected
 */
export const POC_DUTY_SHIFTS = ["day", "night", "both"];
export function isValidPocDutyShift(value) {
    return POC_DUTY_SHIFTS.includes(value);
}
/** Human labels for the three shift settings, shared by admin + agency UIs. */
export const POC_DUTY_SHIFT_LABELS = {
    day: "Day shift only (AM)",
    night: "Night shift only (PM)",
    both: "Both shifts"
};
export const POC_DUTY_CHANGE_REASONS = [
    { code: "patient_declined", label: "Patient isn't interested" },
    { code: "family_will_do", label: "Family member will do it" },
    { code: "patient_requests_more", label: "Patient asked for more help" },
    { code: "patient_requests_less", label: "Patient asked for less help" },
    { code: "aide_not_needed", label: "Aide not needed for this duty" }
];
export const SERVICE_TYPES = ["hha", "pca", "ahha", "hcss"];
/**

/**
 * Merges AI-suggested duties with the master duty list.
 * Guarantees every duty for the service type is present.
 * AI omissions appear as unselected; AI hallucinations are discarded.
 */
/**
 * Checks if a frequency string is a valid POC frequency.
 */
export function isValidPocFrequency(freq) {
    return POC_FREQUENCIES.includes(freq);
}
/**
 * True only for the "N visits per week" members of POC_FREQUENCIES — the shape
 * an agency CONSTRAINT can demand ("… can only both be checked if RN provides
 * exact visits per week for each"). "Every visit" and "As needed" are catalog
 * defaults, never something a constraint asks a model to compute, so they are
 * excluded. See generate-poc.ts for the one caller and why the distinction
 * matters there.
 */
export function isExplicitWeeklyPocFrequency(freq) {
    return isValidPocFrequency(freq) && freq.endsWith("visits per week");
}
/**
 * Returns the canonical ServiceType for behavior/comparison purposes.
 * Case- and whitespace-insensitive. Unknown input falls back to "pca" and warns.
 * Use ONLY for behavior — for display, use the raw value.
 */
export function normalizeServiceType(raw) {
    if (raw != null) {
        const lower = raw.trim().toLowerCase();
        if (SERVICE_TYPES.includes(lower)) {
            return lower;
        }
    }
    if (raw != null && raw.trim() !== "") {
        console.warn(`[normalizeServiceType] Unknown service type "${raw}" — falling back to "pca".`);
    }
    return "pca";
}
export const POC_PDF_DUTY_GROUPS = [
    // ─── Personal Care ─────────────────────────────────────
    {
        label: "Shower",
        code: "101",
        category: "Personal Care",
        mainDuty: "Bath - Shower",
        subOptions: [
            { label: "Bath Shower", dutyName: "Bath - Shower" },
            { label: "Bath Tub", dutyName: "Bath - Bath Tub", code: "100" },
            { label: "Bed Bath", dutyName: "Bath - Bed Bath", code: "102" }
        ]
    },
    {
        label: "Hair Care - Shampoo",
        code: "108",
        category: "Personal Care",
        mainDuty: "Hair Care - Shampoo"
    },
    {
        label: "Hair Care - Comb/Brush",
        code: "107",
        category: "Personal Care",
        mainDuty: "Hair Care - Comb/Brush"
    },
    {
        label: "Mouth Care/Denture Care",
        code: "106",
        category: "Personal Care",
        mainDuty: "Mouth Care / Denture Care"
    },
    {
        label: "Grooming-Shave",
        code: "109",
        category: "Personal Care",
        mainDuty: "Grooming - Shave"
    },
    {
        label: "Grooming-Nails (file only- do not cut)",
        code: "110",
        category: "Personal Care",
        mainDuty: "Grooming - Nails (file only, do not cut)"
    },
    {
        label: "Dressing",
        code: "111",
        category: "Personal Care",
        mainDuty: "Dressing - Partial",
        subOptions: [
            { label: "Partial Dressing", dutyName: "Dressing - Partial" },
            { label: "Total Dressing", dutyName: "Dressing - Total" }
        ]
    },
    { label: "Skin Care", code: "112", category: "Personal Care", mainDuty: "Skin Care" },
    { label: "Foot Care", code: "113", category: "Personal Care", mainDuty: "Foot Care" },
    // ─── Toileting ─────────────────────────────────────────
    {
        label: "Toileting",
        code: "117",
        category: "Toileting",
        mainDuty: "Toileting - Bathroom",
        subOptions: [
            { label: "Bathroom", dutyName: "Toileting - Bathroom" },
            { label: "Commode", dutyName: "Toileting - Commode", code: "115" },
            { label: "Bedpan", dutyName: "Toileting - Bedpan" },
            { label: "Urinal", dutyName: "Toileting - Urinal", code: "116" }
        ]
    },
    {
        label: "Incontinence",
        code: "114",
        category: "Toileting",
        mainDuty: "Incontinence - Diapers",
        subOptions: [
            { label: "Diapers", dutyName: "Incontinence - Diapers" },
            { label: "Other Incontinence Supplies", dutyName: "Incontinence - Other Supplies" }
        ]
    },
    {
        label: "Record Output",
        code: "407",
        category: "Toileting",
        mainDuty: "Record Output - Urine",
        subOptions: [
            { label: "Urine", dutyName: "Record Output - Urine" },
            { label: "Bowel Movement", dutyName: "Record Output - Bowel Movement" }
        ]
    },
    { label: "Perineal Care", code: "118", category: "Toileting", mainDuty: "Perineal Care" },
    // ─── Nutrition ─────────────────────────────────────────
    {
        label: "Prepare Meals",
        code: "201",
        category: "Nutrition",
        mainDuty: "Prepare Meals",
        subOptions: [
            { label: "Breakfast", dutyName: "Meal - Breakfast", code: "202" },
            { label: "Lunch", dutyName: "Meal - Lunch", code: "203" },
            { label: "Dinner", dutyName: "Meal - Dinner", code: "204" },
            { label: "Snack", dutyName: "Meal - Snack", code: "205" }
        ]
    },
    {
        label: "Assist with Feeding",
        code: "206",
        category: "Nutrition",
        mainDuty: "Assist with Feeding"
    },
    {
        label: "Record Intake",
        code: "",
        category: "Nutrition",
        mainDuty: "Record Intake - Food",
        subOptions: [
            { label: "Food", dutyName: "Record Intake - Food", code: "207" },
            { label: "Fluid", dutyName: "Record Intake - Fluid", code: "208" }
        ]
    },
    {
        label: "Patient is on a prescribed diet",
        code: "209",
        category: "Nutrition",
        mainDuty: "Patient on Prescribed Diet"
    },
    // ─── Activities ────────────────────────────────────────
    {
        label: "Transferring",
        code: "300",
        category: "Activities",
        mainDuty: "Transferring - Hoyer Lift",
        subOptions: [
            { label: "Hoyer Lift", dutyName: "Transferring - Hoyer Lift" },
            { label: "Wheelchair", dutyName: "Transferring - Wheelchair" }
        ]
    },
    {
        label: "Assist with Walking",
        code: "301",
        category: "Activities",
        mainDuty: "Assist with Walking"
    },
    {
        label: "Patient walks with assisted device",
        code: "302",
        category: "Activities",
        mainDuty: "Assisted Device - Walker",
        hhaLabel: "Patient uses assisted device",
        subOptions: [
            { label: "Walker", dutyName: "Assisted Device - Walker" },
            { label: "Cane", dutyName: "Assisted Device - Cane" },
            { label: "Crutches", dutyName: "Assisted Device - Crutches" }
        ]
    },
    {
        label: "Assist with home exercise program",
        code: "305",
        category: "Activities",
        mainDuty: "Assist with Home Exercise Program"
    },
    {
        label: "Turning/Positioning - At least every 2 hours",
        code: "311",
        category: "Activities",
        mainDuty: "Turning/Positioning - At least every 2 hours"
    },
    {
        label: "Assist with ROM Exercises",
        code: "306",
        category: "Activities",
        mainDuty: "Assist with ROM Exercises",
        serviceTypes: ["hha"]
    },
    // ─── Treatment ─────────────────────────────────────────
    {
        label: "Catheter Care (Clean, Empty Bag)",
        code: "408",
        category: "Treatment",
        mainDuty: "Catheter Care (Assist / Clean & Empty Bag)",
        hhaLabel: "Assist with Catheter Care"
    },
    {
        label: "Empty Foley Bag",
        code: "409",
        category: "Treatment",
        mainDuty: "Foley Bag (Assist / Empty)",
        hhaLabel: "Assist with Foley Bag"
    },
    {
        label: "Ostomy Care",
        code: "410",
        category: "Treatment",
        mainDuty: "Ostomy Care",
        serviceTypes: ["hha"]
    },
    {
        label: "Remind to take medication",
        code: "411",
        category: "Treatment",
        mainDuty: "Remind to Take Medication"
    },
    {
        label: "Ask Patient About Pain",
        code: "413",
        category: "Treatment",
        mainDuty: "Ask Patient About Pain"
    },
    {
        label: "Observe/Report Physical/Mental Changes",
        code: "414",
        category: "Treatment",
        mainDuty: "Observe/Report Physical/Mental Changes"
    },
    {
        label: "Monitor Patient Safety",
        code: "511",
        category: "Treatment",
        mainDuty: "Monitor Patient Safety"
    },
    // ─── Household ─────────────────────────────────────────
    { label: "Change Bed Linen", code: "500", category: "Household", mainDuty: "Change Bed Linen" },
    { label: "Patient Laundry", code: "501", category: "Household", mainDuty: "Patient Laundry" },
    {
        label: "Light Housekeeping: (Dust, Vacuum, Clean)",
        code: "502",
        category: "Household",
        mainDuty: "Light Housekeeping (Dust, Vacuum, Clean)"
    },
    {
        label: "Shopping and Errands",
        code: "506",
        category: "Household",
        mainDuty: "Shopping and Errands"
    },
    {
        label: "Accompany Patient to medical appointment",
        code: "508",
        category: "Household",
        mainDuty: "Accompany to Medical Appointment"
    }
];
/**
 * Returns the PDF duty groups filtered for a service type.
 */
export function getPocPdfDutyGroups(serviceType) {
    return POC_PDF_DUTY_GROUPS.filter((row) => !row.serviceTypes || row.serviceTypes.includes(serviceType));
}
// ─── Visit-type rules ────────────────────────────────────
export { SUPPORTED_TASK_TYPES, SPLIT_SHIFT_VISIT_TYPES, SPLIT_SHIFT_WEEKLY_HOURS, isSupportedTaskType, isSplitShiftVisitType, requiresCertificationDates, requiresFrequency, requiresStartOfCareDate, getRequiredFields, parseVisitFrequencyHours, getSplitShiftFrequencyWarning } from "./visit-type-rules.js";
export { CURATED_TYPE_LABELS, GENERIC_SPECIAL_VISIT_LABEL, specialVisitLabel } from "./special-visit-label.js";
// ─── Aide present at the visit (supervisory form answer) ──
export { aidePresentLabel, aidePresentNotes, aidePresentYesNo } from "./aide-present.js";
// ─── Agency legal name vs DBA (the name patients and caregivers hear) ──
export { agencyPublicName, dbaAfterLegalRename, deriveAgencyDba, normalizeAgencyDbaInput } from "./agency-name.js";
// ─── Agency Reporting export columns (CSV / Excel / print) ──
export { REPORT_EXPORT_COLUMNS, REPORT_EXPORT_COLUMN_KEYS, REPORT_EXPORT_COLUMN_OPTIONS, DEFAULT_REPORT_EXPORT_COLUMNS, isReportExportColumnKey, normalizeReportExportColumns, reportExportColumnLabel, reportExportCell, reportExportHeader, reportExportRow } from "./report-export-columns.js";
// ─── Medication extraction failure kinds ─────────────────
export { MEDICATION_EXTRACTION_FAILURE_KINDS, NO_MEDICATIONS_FOUND_MESSAGE, EXTRACTION_ERROR_MESSAGE, isMedicationExtractionFailureKind } from "./medication-extraction.js";
export { sortIcdSuggestionsByLikelihood, mostLikelyIcdSuggestion } from "./icd-likelihood.js";
// ─── Visit flags (needs_attention + missed_visit) ────────
export { NEEDS_ATTENTION_REASONS, MISSED_VISIT_FAULTS, TRAVEL_PAY_MISSED_FAULTS, ON_SITE_ISSUE_REASONS, ON_SITE_ISSUE_MISS_FAULT, getNeedsAttentionLabel, isTravelPayMissedFault, getOnSiteIssueLabel, isSupervisoryVisitTypeName } from "./visit-flags.js";
// ─── Visit display status + confirmation stripe (derived, never stored) ──
export { VISIT_LIFECYCLE_STATUSES, VISIT_STRIPE_COLORS, VISIT_DISPLAY_BUCKETS, VISIT_DISPLAY_BUCKET_LABELS, deriveDisplayStatus, visitStripeState, isVisitDisplayBucket, matchesDisplayBucket } from "./visit-display-status.js";
// ─── Final due date (the real last day, when the due date has slack) ──
export { effectiveFinalDue, FINAL_DUE_MAX_DAYS, finalDueAfterDays, finalDueDateError, visitDeadline } from "./final-due.js";
// ─── Certification period (incl. "based on the visit date") ──
export { CERTIFICATION_PERIOD_DAYS, certificationPeriodFromVisitDate, isVisitDateCertificationCell } from "./certification-period.js";
// ─── Geo / distance (clock-in/out proximity) ─────────────
export { CLOCK_DISTANCE_THRESHOLD_FEET, CLOCK_DISTANCE_UNRELIABLE_FEET, TRAIL_PRESENCE_RADIUS_FEET, GEOCODE_DISAGREEMENT_FEET, distanceFeet, metersToFeet, computeClockDistance, isClockLocationFar, isBeyondClockThreshold } from "./geo.js";
export { rnChannel, rnAllChannel, agencyChannel, adminAllChannel } from "./realtime/index.js";
export { CONFIDENCE_RANK } from "./diagnosis-matrix.js";
// ─── Branching engine (shared with mobile; used by the visit ground-truth loader) ──
export { evaluateVisibility, computeDepths, computeRenderOrder } from "./branching.js";
// ─── Urgent (safety) escalations (API labels them, Admin Hub badges them) ────
export { URGENT_ESCALATION_PREFIX, isUrgentEscalation, markUrgentEscalationReason } from "./escalation.js";
// ─── RN contract document (shared by the mobile reader and the signed PDF) ────
export { parseContractDoc, groupContractSections, applyContractMergeFields, contractBlockText, BLANK_FIELD } from "./contract-doc.js";
export * from "./context-hint-policy.js";
export { frequencyFromAvailabilitySlots } from "./availability-frequency.js";
export { PHYSICIAN_NEEDS_VERIFICATION, physicianSearchInputError, hasFullPhysicianName, isPhysicianPending } from "./physician-verification.js";
export { medicationDisplay } from "./clinical-profile.js";
// ─── HHAeXchange upload report (API + Admin Hub + Agency Portal) ──
export * from "./hhax-upload-report.js";
//# sourceMappingURL=index.js.map